// Accounting: Dashboard, Chart of Accounts, Journal Entries, Trial Balance, P&L, Balance Sheet, Tax, Expenses, Deposits, Money Transfers
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Search, X, Save, Wallet, BookOpen, ListChecks, Scale,
  TrendingUp, PieChart, FileText, ArrowLeftRight, ArrowDownCircle, ArrowUpCircle,
  Receipt, FolderTree,
} from "lucide-react";
import { api } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader } from "../products/_shared";
import { fmtMoney as fmt } from "../../../lib/currency";

// ────────────────────────── DASHBOARD ──────────────────────────
export function AccDashboard() {
  const { subdomain } = useParams();
  const [d, setD] = useState({});
  useEffect(() => { api.get("/t/acc/overview").then((r) => setD(r.data)); }, []);
  const tiles = [
    { lbl: "Accounts", v: d.accounts_count, color: "text-[#1FA868]", bg: "bg-[#E8F5E9]", icon: BookOpen },
    { lbl: "Journal Entries (30d)", v: d.journal_count_30d, color: "text-sky-700", bg: "bg-sky-50", icon: ListChecks },
    { lbl: "Income (30d)", v: fmt(d.income_30d), color: "text-emerald-700", bg: "bg-emerald-50", icon: ArrowDownCircle, isFmt: true },
    { lbl: "Expense (30d)", v: fmt(d.expense_30d), color: "text-rose-700", bg: "bg-rose-50", icon: ArrowUpCircle, isFmt: true },
  ];
  const qActions = [
    { lbl: "Chart of Accounts", to: "accounting/accounts", icon: BookOpen },
    { lbl: "Journal Entries", to: "accounting/journal-entries", icon: ListChecks },
    { lbl: "Trial Balance", to: "accounting/trial-balance", icon: Scale },
    { lbl: "Profit & Loss", to: "accounting/pl", icon: TrendingUp },
    { lbl: "Balance Sheet", to: "accounting/balance-sheet", icon: PieChart },
    { lbl: "Tax Summary", to: "accounting/tax-summary", icon: FileText },
  ];
  // find max for mini-chart
  const series = d.series || [];
  const maxVal = Math.max(1, ...series.flatMap((s) => [s.income || 0, s.expense || 0]));
  return (
    <div data-testid="acc-dashboard-page" className="p-6 lg:p-8">
      <PageHeader title="Advanced Accounting" breadcrumb="Accounting / Dashboard" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.lbl} className="p-5 border-l-4 border-l-[#1FA868]">
              <div className={`w-10 h-10 rounded-xl ${t.bg} ${t.color} flex items-center justify-center`}><Icon size={18} /></div>
              <div className="text-xs text-black/55 mt-3">{t.lbl}</div>
              <div className={`text-2xl font-extrabold tracking-tight ${t.color} mt-1`}>{t.v ?? 0}</div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5 mb-6">
        <SectionHeader icon={Wallet} title="Quick Actions" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {qActions.map((q) => {
            const Icon = q.icon;
            return (
              <Link key={q.lbl} to={`/t/${subdomain}/${q.to}`}
                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-[#1FA868]/20 hover:bg-[#E8F5E9] transition-colors">
                <Icon size={15} className="text-[#1FA868]" />
                <span className="text-sm font-semibold text-black">{q.lbl}</span>
              </Link>
            );
          })}
        </div>
      </Card>

      <Card className="p-5">
        <SectionHeader icon={TrendingUp} title="Income vs Expense (30d)" />
        {series.length ? (
          <div className="h-52 flex items-end gap-0.5">
            {series.map((s) => (
              <div key={s.date} className="flex-1 flex flex-col items-center gap-0.5 relative group">
                <div className="w-full flex flex-col justify-end h-48">
                  <div className="w-full bg-emerald-500/70 rounded-t" style={{ height: `${((s.income || 0) / maxVal) * 100}%` }} />
                  <div className="w-full bg-rose-500/70 mt-0.5" style={{ height: `${((s.expense || 0) / maxVal) * 100}%` }} />
                </div>
                <div className="absolute -bottom-4 text-[8px] text-black/50 rotate-45 origin-left">{s.date.slice(5)}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-center text-black/45 py-8">No data</div>}
        <div className="flex gap-4 mt-6 text-xs">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500/70" /> Income</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-rose-500/70" /> Expense</div>
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────── CHART OF ACCOUNTS ──────────────────────────
export function AccAccounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ type: "asset", active: true });
  const load = () => api.get("/t/acc/accounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const openCreate = () => { setForm({ code: "", name: "", type: "asset", description: "", active: true }); setEdit(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEdit(r); setOpen(true); };
  const submit = async () => {
    try {
      if (edit) await api.patch(`/t/acc/accounts/${edit.id}`, form);
      else await api.post("/t/acc/accounts", form);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete account ${r.code} ${r.name}?`)) return;
    await api.delete(`/t/acc/accounts/${r.id}`);
    toast.success("Deleted"); load();
  };
  const typeColor = (t) => ({ asset: "bg-sky-100 text-sky-700", liability: "bg-amber-100 text-amber-700", equity: "bg-violet-100 text-violet-700", income: "bg-emerald-100 text-emerald-700", expense: "bg-rose-100 text-rose-700" })[t];
  return (
    <div data-testid="acc-accounts-page" className="p-6 lg:p-8">
      <PageHeader title="Chart of Accounts" breadcrumb="Accounting / Chart of Accounts" actions={
        <PrimaryButton icon={Plus} onClick={openCreate} testid="acc-accounts-new">Add Account</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Code</th>
              <th className="text-left p-3.5">Name</th>
              <th className="text-left p-3.5">Type</th>
              <th className="text-left p-3.5">Description</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5 font-mono font-semibold">{r.code}</td>
                <td className="p-3.5 font-semibold">{r.name}</td>
                <td className="p-3.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${typeColor(r.type)}`}>{r.type}</span></td>
                <td className="p-3.5 text-black/60">{r.description || "—"}</td>
                <td className="p-3.5 text-right">
                  <button onClick={() => openEdit(r)} className="p-1.5 rounded hover:bg-black/5 mr-1 text-xs">Edit</button>
                  <button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">{edit ? "Edit" : "New"} Account</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field lbl="Code" required><TextInput value={form.code} onChange={(v) => setForm((x) => ({ ...x, code: v }))} placeholder="1000" testid="acc-f-code" /></Field>
              <Field lbl="Name" required><TextInput value={form.name} onChange={(v) => setForm((x) => ({ ...x, name: v }))} testid="acc-f-name" /></Field>
              <Field lbl="Type"><Select value={form.type} onChange={(v) => setForm((x) => ({ ...x, type: v }))} options={["asset", "liability", "equity", "income", "expense"].map((t) => ({ value: t, label: t }))} testid="acc-f-type" /></Field>
              <Field lbl="Description"><textarea value={form.description || ""} onChange={(e) => setForm((x) => ({ ...x, description: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="acc-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── JOURNAL ENTRIES ──────────────────────────
export function AccJournal() {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), reference: "", memo: "", lines: [{ account_id: "", debit: 0, credit: 0, memo: "" }, { account_id: "", debit: 0, credit: 0, memo: "" }] });
  const load = () => api.get("/t/acc/journal-entries").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/acc/accounts").then((r) => setAccounts(r.data)); }, []);
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { account_id: "", debit: 0, credit: 0, memo: "" }] }));
  const updLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const totalD = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalC = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const submit = async () => {
    if (Math.abs(totalD - totalC) > 0.01) return toast.error(`Unbalanced: Debit ${totalD} ≠ Credit ${totalC}`);
    try {
      await api.post("/t/acc/journal-entries", form);
      toast.success("Journal entry posted");
      setOpen(false); load();
      setForm({ date: new Date().toISOString().slice(0, 10), reference: "", memo: "", lines: [{ account_id: "", debit: 0, credit: 0, memo: "" }, { account_id: "", debit: 0, credit: 0, memo: "" }] });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete entry?")) return;
    await api.delete(`/t/acc/journal-entries/${r.id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="acc-journal-page" className="p-6 lg:p-8">
      <PageHeader title="Journal Entries" breadcrumb="Accounting / Journal Entries" actions={
        <PrimaryButton icon={Plus} onClick={() => setOpen(true)} testid="acc-journal-new">New Entry</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Memo</th>
              <th className="text-right p-3.5">Debit</th>
              <th className="text-right p-3.5">Credit</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5">{r.date}</td>
                <td className="p-3.5 font-semibold">{r.reference || "—"}</td>
                <td className="p-3.5 text-black/70 max-w-sm truncate">{r.memo || "—"}</td>
                <td className="p-3.5 text-right font-semibold">{fmt(r.total_debit)}</td>
                <td className="p-3.5 text-right font-semibold">{fmt(r.total_credit)}</td>
                <td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No journal entries yet</td></tr>}
          </tbody>
        </table>
      </Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">New Journal Entry</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Field lbl="Date"><TextInput type="date" value={form.date} onChange={(v) => setForm((x) => ({ ...x, date: v }))} testid="acc-j-date" /></Field>
                <Field lbl="Reference"><TextInput value={form.reference} onChange={(v) => setForm((x) => ({ ...x, reference: v }))} placeholder="e.g. INV-001" /></Field>
                <Field lbl="Memo"><TextInput value={form.memo} onChange={(v) => setForm((x) => ({ ...x, memo: v }))} /></Field>
              </div>
              <div>
                <div className="text-xs font-bold uppercase text-black/55 mb-2">Lines</div>
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFBFB] text-[10px] uppercase text-black/55">
                    <tr>
                      <th className="text-left p-2">Account</th>
                      <th className="text-right p-2 w-28">Debit</th>
                      <th className="text-right p-2 w-28">Credit</th>
                      <th className="text-left p-2">Memo</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {form.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="p-2">
                          <select value={l.account_id} onChange={(e) => updLine(i, "account_id", e.target.value)} data-testid={`acc-j-line-${i}-acc`}
                            className="w-full px-2 py-1.5 rounded border border-black/15 text-sm">
                            <option value="">Select...</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                          </select>
                        </td>
                        <td className="p-2 text-right"><input type="number" value={l.debit} onChange={(e) => updLine(i, "debit", Number(e.target.value))} className="w-24 text-right px-2 py-1.5 rounded border border-black/15" /></td>
                        <td className="p-2 text-right"><input type="number" value={l.credit} onChange={(e) => updLine(i, "credit", Number(e.target.value))} className="w-24 text-right px-2 py-1.5 rounded border border-black/15" /></td>
                        <td className="p-2"><input value={l.memo} onChange={(e) => updLine(i, "memo", e.target.value)} className="w-full px-2 py-1.5 rounded border border-black/15" /></td>
                        <td className="p-2 text-right"><button onClick={() => rmLine(i)} className="text-rose-600"><Trash2 size={13} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={addLine} className="mt-2 text-xs font-bold text-[#1FA868]">+ Add Line</button>
              </div>
              <div className="bg-[#E8F5E9] rounded-lg p-3 flex justify-between items-center text-sm">
                <span>Totals</span>
                <div className="flex gap-4">
                  <span>Debit: <span className="font-extrabold">{fmt(totalD)}</span></span>
                  <span>Credit: <span className="font-extrabold">{fmt(totalC)}</span></span>
                  <span className={`font-extrabold ${Math.abs(totalD - totalC) < 0.01 ? "text-[#1FA868]" : "text-rose-600"}`}>
                    {Math.abs(totalD - totalC) < 0.01 ? "Balanced ✓" : `Diff ${fmt(Math.abs(totalD - totalC))}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="acc-j-save">Post Entry</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── TRIAL BALANCE ──────────────────────────
export function AccTrialBalance() {
  const [d, setD] = useState({ rows: [], total_debit: 0, total_credit: 0 });
  useEffect(() => { api.get("/t/acc/trial-balance").then((r) => setD(r.data)); }, []);
  return (
    <div data-testid="acc-trial-page" className="p-6 lg:p-8">
      <PageHeader title="Trial Balance" breadcrumb="Accounting / Trial Balance" />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Code</th>
              <th className="text-left p-3.5">Name</th>
              <th className="text-left p-3.5">Type</th>
              <th className="text-right p-3.5">Debit</th>
              <th className="text-right p-3.5">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {d.rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5 font-mono">{r.code}</td>
                <td className="p-3.5 font-semibold">{r.name}</td>
                <td className="p-3.5 text-black/60">{r.type}</td>
                <td className="p-3.5 text-right font-semibold">{r.debit ? fmt(r.debit) : "—"}</td>
                <td className="p-3.5 text-right font-semibold">{r.credit ? fmt(r.credit) : "—"}</td>
              </tr>
            ))}
            {!d.rows.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No postings yet</td></tr>}
            <tr className="bg-[#E8F5E9] font-extrabold">
              <td colSpan={3} className="p-3.5">Totals</td>
              <td className="p-3.5 text-right">{fmt(d.total_debit)}</td>
              <td className="p-3.5 text-right">{fmt(d.total_credit)}</td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ────────────────────────── PROFIT & LOSS ──────────────────────────
export function AccPL() {
  const [d, setD] = useState({ income: [], expense: [], total_income: 0, total_expense: 0, net_profit: 0 });
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const load = () => api.get("/t/acc/pl", { params: { start_date: start || undefined, end_date: end || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div data-testid="acc-pl-page" className="p-6 lg:p-8">
      <PageHeader title="Profit & Loss" breadcrumb="Accounting / Profit & Loss" actions={
        <div className="flex gap-2 items-center">
          <TextInput type="date" value={start} onChange={setStart} testid="acc-pl-start" />
          <TextInput type="date" value={end} onChange={setEnd} testid="acc-pl-end" />
          <PrimaryButton onClick={load}>Apply</PrimaryButton>
        </div>
      } />
      <div className="grid md:grid-cols-2 gap-5">
        <Card className="p-5">
          <SectionHeader icon={ArrowDownCircle} title="Income" />
          <table className="w-full text-sm">
            <tbody className="divide-y divide-black/5">
              {d.income.map((a) => (
                <tr key={a.id}><td className="p-2">{a.code} · {a.name}</td><td className="p-2 text-right font-semibold">{fmt(a.amount)}</td></tr>
              ))}
              {d.simple_deposit_total > 0 && <tr><td className="p-2 text-black/60">Misc. Deposits</td><td className="p-2 text-right font-semibold">{fmt(d.simple_deposit_total)}</td></tr>}
              {!d.income.length && !d.simple_deposit_total && <tr><td className="p-6 text-center text-black/45" colSpan={2}>No income</td></tr>}
              <tr className="bg-emerald-50 font-extrabold"><td className="p-2">Total Income</td><td className="p-2 text-right text-emerald-700">{fmt(d.total_income)}</td></tr>
            </tbody>
          </table>
        </Card>
        <Card className="p-5">
          <SectionHeader icon={ArrowUpCircle} title="Expenses" />
          <table className="w-full text-sm">
            <tbody className="divide-y divide-black/5">
              {d.expense.map((a) => (
                <tr key={a.id}><td className="p-2">{a.code} · {a.name}</td><td className="p-2 text-right font-semibold">{fmt(a.amount)}</td></tr>
              ))}
              {d.simple_expense_total > 0 && <tr><td className="p-2 text-black/60">Misc. Expenses</td><td className="p-2 text-right font-semibold">{fmt(d.simple_expense_total)}</td></tr>}
              {!d.expense.length && !d.simple_expense_total && <tr><td className="p-6 text-center text-black/45" colSpan={2}>No expenses</td></tr>}
              <tr className="bg-rose-50 font-extrabold"><td className="p-2">Total Expenses</td><td className="p-2 text-right text-rose-700">{fmt(d.total_expense)}</td></tr>
            </tbody>
          </table>
        </Card>
      </div>
      <Card className="p-5 mt-5 bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase opacity-80">Net Profit</div>
            <div className="text-4xl font-extrabold mt-1">{fmt(d.net_profit)}</div>
          </div>
          <TrendingUp size={48} className="opacity-70" />
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────── BALANCE SHEET ──────────────────────────
export function AccBalanceSheet() {
  const [d, setD] = useState({ assets: [], liabilities: [], equity: [], total_assets: 0, total_liabilities: 0, total_equity: 0 });
  useEffect(() => { api.get("/t/acc/balance-sheet").then((r) => setD(r.data)); }, []);
  const Section = ({ title, rows, total, color }) => (
    <Card className="p-5">
      <div className={`text-sm font-bold uppercase mb-3 ${color}`}>{title}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-black/5">
          {rows.map((a) => (<tr key={a.id}><td className="p-2">{a.code} · {a.name}</td><td className="p-2 text-right font-semibold">{fmt(a.amount)}</td></tr>))}
          {!rows.length && <tr><td className="p-6 text-center text-black/45" colSpan={2}>None</td></tr>}
          <tr className="font-extrabold bg-black/5"><td className="p-2">Total</td><td className="p-2 text-right">{fmt(total)}</td></tr>
        </tbody>
      </table>
    </Card>
  );
  return (
    <div data-testid="acc-bs-page" className="p-6 lg:p-8">
      <PageHeader title="Balance Sheet" breadcrumb="Accounting / Balance Sheet" />
      <div className="grid lg:grid-cols-3 gap-5">
        <Section title="Assets" rows={d.assets} total={d.total_assets} color="text-sky-700" />
        <Section title="Liabilities" rows={d.liabilities} total={d.total_liabilities} color="text-amber-700" />
        <Section title="Equity" rows={d.equity} total={d.total_equity} color="text-violet-700" />
      </div>
      <Card className="p-5 mt-5 bg-[#E8F5E9]">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><div className="text-xs uppercase text-black/55">Assets</div><div className="text-xl font-extrabold text-[#1FA868]">{fmt(d.total_assets)}</div></div>
          <div><div className="text-xs uppercase text-black/55">Liab + Equity</div><div className="text-xl font-extrabold text-[#1FA868]">{fmt(d.total_liabilities + d.total_equity)}</div></div>
          <div><div className="text-xs uppercase text-black/55">Status</div><div className={`text-xl font-extrabold ${Math.abs(d.total_assets - (d.total_liabilities + d.total_equity)) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>{Math.abs(d.total_assets - (d.total_liabilities + d.total_equity)) < 0.01 ? "Balanced" : "Unbalanced"}</div></div>
        </div>
      </Card>
    </div>
  );
}

// ────────────────────────── TAX SUMMARY ──────────────────────────
export function AccTax() {
  const [d, setD] = useState({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const load = () => api.get("/t/acc/tax-summary", { params: { start_date: start || undefined, end_date: end || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div data-testid="acc-tax-page" className="p-6 lg:p-8">
      <PageHeader title="Tax Summary" breadcrumb="Accounting / Tax Summary" actions={
        <div className="flex gap-2">
          <TextInput type="date" value={start} onChange={setStart} />
          <TextInput type="date" value={end} onChange={setEnd} />
          <PrimaryButton onClick={load}>Apply</PrimaryButton>
        </div>
      } />
      <div className="grid md:grid-cols-3 gap-5">
        <Card className="p-6"><div className="text-xs uppercase text-black/55">Sales Tax Collected</div><div className="text-3xl font-extrabold text-emerald-700 mt-2">{fmt(d.sales_tax_collected)}</div><div className="text-xs text-black/45 mt-1">{d.sales_count || 0} sales</div></Card>
        <Card className="p-6"><div className="text-xs uppercase text-black/55">Purchase Tax Paid</div><div className="text-3xl font-extrabold text-rose-700 mt-2">{fmt(d.purchase_tax_paid)}</div><div className="text-xs text-black/45 mt-1">{d.purchase_count || 0} purchases</div></Card>
        <Card className="p-6 bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white"><div className="text-xs uppercase opacity-80">Net Tax Payable</div><div className="text-3xl font-extrabold mt-2">{fmt(d.net_tax_payable)}</div></Card>
      </div>
    </div>
  );
}

// ────────────────────────── EXPENSES / DEPOSITS / CATEGORIES / MONEY TRANSFERS ──────────────────────────
function MoneyList({ title, breadcrumb, endpoint, testidBase, kind, icon: Icon }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: 0 });
  const load = () => api.get(endpoint).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/t/acc/categories", { params: { kind } }).then((r) => setCats(r.data));
    api.get("/t/acc/accounts").then((r) => setAccounts(r.data.filter((a) => ["asset", "liability"].includes(a.type))));
  }, []); // eslint-disable-line

  const submit = async () => {
    try {
      await api.post(endpoint, form);
      toast.success("Saved"); setOpen(false); load();
      setForm({ date: new Date().toISOString().slice(0, 10), amount: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`${endpoint}/${r.id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid={`${testidBase}-page`} className="p-6 lg:p-8">
      <PageHeader title={title} breadcrumb={breadcrumb} actions={
        <PrimaryButton icon={Plus} onClick={() => setOpen(true)} testid={`${testidBase}-new`}>New</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Category</th>
              <th className="text-left p-3.5">Payment</th>
              <th className="text-right p-3.5">Amount</th>
              <th className="text-left p-3.5">Note</th>
              <th className="text-right p-3.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="p-3.5">{r.date}</td>
                <td className="p-3.5">{r.category_name || "—"}</td>
                <td className="p-3.5 capitalize">{r.payment_method || "—"}</td>
                <td className={`p-3.5 text-right font-bold ${kind === "deposit" ? "text-emerald-700" : "text-rose-700"}`}>{fmt(r.amount)}</td>
                <td className="p-3.5 text-black/60 max-w-xs truncate">{r.note || "—"}</td>
                <td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No entries</td></tr>}
          </tbody>
        </table>
      </Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-black/5">
              <h3 className="font-bold">New {title.replace(/s$/, "")}</h3>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-black/5 rounded"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-3">
              <Field lbl="Date" required><TextInput type="date" value={form.date} onChange={(v) => setForm((x) => ({ ...x, date: v }))} testid={`${testidBase}-f-date`} /></Field>
              <Field lbl="Amount" required><TextInput type="number" value={form.amount} onChange={(v) => setForm((x) => ({ ...x, amount: Number(v) }))} testid={`${testidBase}-f-amount`} /></Field>
              <Field lbl="Category"><Select value={form.category_id || ""} onChange={(v) => setForm((x) => ({ ...x, category_id: v }))} options={cats.map((c) => ({ value: c.id, label: c.name }))} /></Field>
              <Field lbl="Payment Method"><Select value={form.payment_method || "cash"} onChange={(v) => setForm((x) => ({ ...x, payment_method: v }))} options={["cash", "bank", "card", "transfer"].map((m) => ({ value: m, label: m }))} /></Field>
              <Field lbl="Account"><Select value={form.account_id || ""} onChange={(v) => setForm((x) => ({ ...x, account_id: v }))} options={accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))} /></Field>
              <Field lbl="Note"><textarea value={form.note || ""} onChange={(e) => setForm((x) => ({ ...x, note: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid={`${testidBase}-f-save`}>Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export const AccExpenses = () => <MoneyList title="Expenses" breadcrumb="Accounting / Expenses" endpoint="/t/acc/expenses" testidBase="acc-exp" kind="expense" icon={ArrowUpCircle} />;
export const AccDeposits = () => <MoneyList title="Deposits" breadcrumb="Accounting / Deposits" endpoint="/t/acc/deposits" testidBase="acc-dep" kind="deposit" icon={ArrowDownCircle} />;

// Expense / Deposit categories
export function AccCategories({ kind = "expense", title }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind, name: "", description: "" });
  const load = () => api.get("/t/acc/categories", { params: { kind } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  const submit = async () => {
    try {
      await api.post("/t/acc/categories", form);
      toast.success("Created"); setOpen(false); setForm({ kind, name: "", description: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/t/acc/categories/${r.id}`); toast.success("Deleted"); load();
  };
  return (
    <div data-testid={`acc-cat-${kind}-page`} className="p-6 lg:p-8">
      <PageHeader title={title || `${kind === "expense" ? "Expense" : "Deposit"} Categories`} breadcrumb={`Accounting / ${title || "Categories"}`} actions={
        <PrimaryButton icon={Plus} onClick={() => setOpen(true)} testid={`acc-cat-${kind}-new`}>New</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase"><tr><th className="text-left p-3.5">Name</th><th className="text-left p-3.5">Description</th><th className="text-right p-3.5">Actions</th></tr></thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (<tr key={r.id}><td className="p-3.5 font-semibold">{r.name}</td><td className="p-3.5 text-black/60">{r.description || "—"}</td><td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button></td></tr>))}
            {!rows.length && <tr><td colSpan={3} className="p-10 text-center text-black/45">No categories</td></tr>}
          </tbody>
        </table>
      </Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-black/5 flex items-center justify-between"><h3 className="font-bold">New Category</h3><button onClick={() => setOpen(false)}><X size={15} /></button></div>
            <div className="p-5 space-y-3">
              <Field lbl="Name" required><TextInput value={form.name} onChange={(v) => setForm((x) => ({ ...x, name: v }))} testid={`acc-cat-${kind}-f-name`} /></Field>
              <Field lbl="Description"><TextInput value={form.description} onChange={(v) => setForm((x) => ({ ...x, description: v }))} /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid={`acc-cat-${kind}-f-save`}>Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Money Transfers (between accounts)
export function AccMoneyTransfers() {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: 0 });
  const load = () => api.get("/t/acc/money-transfers").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/t/acc/accounts").then((r) => setAccounts(r.data)); }, []);
  const nameOf = (id) => accounts.find((a) => a.id === id)?.name || "—";
  const submit = async () => {
    try {
      await api.post("/t/acc/money-transfers", form);
      toast.success("Saved"); setOpen(false); load();
      setForm({ date: new Date().toISOString().slice(0, 10), amount: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/t/acc/money-transfers/${r.id}`); toast.success("Deleted"); load();
  };
  return (
    <div data-testid="acc-mt-page" className="p-6 lg:p-8">
      <PageHeader title="Money Transfers" breadcrumb="Accounting / Transfers Money" actions={
        <PrimaryButton icon={Plus} onClick={() => setOpen(true)} testid="acc-mt-new">New</PrimaryButton>
      } />
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase"><tr><th className="text-left p-3.5">Date</th><th className="text-left p-3.5">From</th><th className="text-left p-3.5">To</th><th className="text-right p-3.5">Amount</th><th className="text-right p-3.5">Actions</th></tr></thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (<tr key={r.id}><td className="p-3.5">{r.date}</td><td className="p-3.5">{nameOf(r.from_account_id)}</td><td className="p-3.5">{nameOf(r.to_account_id)}</td><td className="p-3.5 text-right font-bold">{fmt(r.amount)}</td><td className="p-3.5 text-right"><button onClick={() => del(r)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={13} /></button></td></tr>))}
            {!rows.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No transfers</td></tr>}
          </tbody>
        </table>
      </Card>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-black/5 flex items-center justify-between"><h3 className="font-bold">Transfer Money</h3><button onClick={() => setOpen(false)}><X size={15} /></button></div>
            <div className="p-5 space-y-3">
              <Field lbl="Date"><TextInput type="date" value={form.date} onChange={(v) => setForm((x) => ({ ...x, date: v }))} /></Field>
              <Field lbl="From Account" required><Select value={form.from_account_id || ""} onChange={(v) => setForm((x) => ({ ...x, from_account_id: v }))} options={accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))} testid="acc-mt-f-from" /></Field>
              <Field lbl="To Account" required><Select value={form.to_account_id || ""} onChange={(v) => setForm((x) => ({ ...x, to_account_id: v }))} options={accounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))} testid="acc-mt-f-to" /></Field>
              <Field lbl="Amount" required><TextInput type="number" value={form.amount} onChange={(v) => setForm((x) => ({ ...x, amount: Number(v) }))} testid="acc-mt-f-amount" /></Field>
              <Field lbl="Note"><textarea value={form.note || ""} onChange={(e) => setForm((x) => ({ ...x, note: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" /></Field>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-black/5">
              <button onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm">Cancel</button>
              <PrimaryButton icon={Save} onClick={submit} testid="acc-mt-f-save">Save</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
